import { useEffect, useRef, useState } from 'react';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import { PortalApiError, safeErrorMessage } from '@/lib/portal-api';
import {
  bookAppointmentForPatient,
  fetchPatientBookingOptions,
  fetchPatientBookingDetails,
  type BookedAppointmentResult,
  type PatientBookingSelection,
  type PatientBookingOptions,
  type PatientBookingDetails,
} from '@/dashboard/api';
import { useClinicContext } from '@/dashboard/clinic-context';
import { AcButton, AcSelect, FormField } from '@/dashboard/ui';

const VISIT_TYPES = [
  ['new_appointment', 'New appointment'],
  ['paid_followup', 'Paid follow-up'],
  ['free_consultation', 'Free consultation'],
  ['surgery', 'Surgery'],
  ['other', 'Other'],
] as const;
const CHANNELS = [
  ['walkin', 'Walk-in'],
  ['telephonic', 'Phone'],
  ['online', 'Online'],
] as const;
interface Attempt {
  appointmentTime: string;
  walkIn: boolean;
  selection: PatientBookingSelection;
  originWalkinId?: number | null;
}
interface SavedDraft {
  attempt: Attempt;
  result?: BookedAppointmentResult;
}

/** Same actor-keyed session draft posture as registration. A sent intent is
 * immutable until its outcome is known; reload never generates a second key. */
export function usePatientBookingFields(
  enabled: boolean,
  patientId: string,
  clinicId: number | null,
  bookingSummary?: { umr: string | null },
  careAppointmentId?: number,
  careCaseSheetId?: number,
  requested?: { doctorId: number | null; durationMinutes: number | null },
) {
  const clinic = useClinicContext();
  const [options, setOptions] = useState<PatientBookingOptions | null>(null);
  const [selection, setSelection] = useState<PatientBookingSelection | null>(null);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [restored, setRestored] = useState<BookedAppointmentResult | null>(null);
  const [savedDetails, setSavedDetails] = useState<PatientBookingDetails | null>(null);
  const [careLabel,setCareLabel]=useState<string|null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const key = useRef<string | null>(null);
  const completedKey = useRef<string | null>(null);
  const clearConfirmed = () => {
    if (completedKey.current) {
      try {
        sessionStorage.removeItem(completedKey.current);
      } catch {
        /* Saved server truth remains. */
      }
    }
  };
  useEffect(() => () => clearConfirmed(), []);
  const lock = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    const current = ++generation.current;
    setReady(false);
    setCareLabel(null);
    setOptions(null);
    setSelection(null);
    setAttempt(null);
    setRestored(null);
    setSavedDetails(null);
    setError(null);
    setBusy(false);
    key.current = null;
    completedKey.current = null;
    lock.current = false;
    if (!enabled || clinicId === null) return;
    let active = true;
    void (async () => {
      const client = getSupabaseBrowserClient();
      const actor = client ? (await client.auth.getSession()).data.session?.user.id : null;
      if (!actor) throw new Error('Session unavailable');
      const draftKey = `patient-booking:v1:${actor}:${clinicId}:${patientId}${careAppointmentId === undefined ? '' : `:care:${careAppointmentId}:${careCaseSheetId}`}`;
      const raw = sessionStorage.getItem(draftKey);
      let previous: SavedDraft | null = null;
      if (raw !== null) {
        previous = JSON.parse(raw) as SavedDraft;
        if (
          !previous?.attempt?.selection?.bookingRequestId ||
          !Number.isFinite(Date.parse(previous.attempt.appointmentTime))
        ) {
          throw new Error(
            'Saved booking request cannot be read. Review the patient’s appointments before booking again.',
          );
        }
      }
      // Restored uncertain requests must remain retryable even if the current
      // catalog is deactivated or unavailable. The server checks their receipt.
      let loaded: PatientBookingOptions | null = null;
      try {
        loaded = await fetchPatientBookingOptions(patientId, clinicId);
      } catch (cause) {
        if (!previous) throw cause;
      }
      let linkedCareLabel: string | null = null;
      if(careAppointmentId!==undefined){
        const parent=await fetchPatientBookingDetails(careAppointmentId);
        if(parent.patientId!==patientId||parent.clinicId!==clinicId)throw new Error('Saved care is unavailable for this patient and clinic.');
        linkedCareLabel=parent.serviceName ?? 'Saved care';
      }
      if (!active || generation.current !== current) return;
      setCareLabel(linkedCareLabel);
      key.current = draftKey;
      setOptions(loaded);
      setSelection(
        previous?.attempt.selection ?? {
          bookingRequestId: crypto.randomUUID(),
          ...(careAppointmentId===undefined?{}:{careAppointmentId,careCaseSheetId}),
          doctorId: requested ? requested.doctorId : loaded?.defaultDoctorId ?? null,
          ...(requested?.durationMinutes == null ? {} : {durationMinutes: requested.durationMinutes}),
          visitType: null,
          bookingChannel: null,
          serviceCode: null,
          department: null,
        },
      );
      if (previous) {
        setAttempt(previous.attempt);
        setRestored(previous.result ?? null);
        if (previous.result) completedKey.current = draftKey;
      }
      setReady(true);
    })().catch((cause) => {
      if (active)
        setError(safeErrorMessage(cause, 'Booking details unavailable. Try again before booking.'));
    });
    return () => {
      active = false;
      generation.current++;
    };
  }, [enabled, patientId, clinicId, reload, careAppointmentId, careCaseSheetId, requested?.doctorId, requested?.durationMinutes]);
  const readSaved = async (id: number) => {
    const current = generation.current;
    try {
      const details = await fetchPatientBookingDetails(id);
      if (current !== generation.current) return;
      if (
        details.patientId !== patientId ||
        details.clinicId !== clinicId ||
        details.appointmentId !== id
      )
        throw new Error('Wrong visit');
      setSavedDetails(details);
      setError(null);
    } catch {
      if (current === generation.current)
        setError('Appointment saved. Booking details unavailable — try reading them again.');
    }
  };
  useEffect(() => {
    if (restored) void readSaved(restored.appointment.id);
  }, [restored]); // eslint-disable-line react-hooks/exhaustive-deps
  const book = async (
    appointmentTime: string,
    walkIn: boolean,
    originWalkinId?: number | null,
  ): Promise<BookedAppointmentResult> => {
    if (lock.current || !ready || !selection || !key.current || clinicId === null)
      throw new Error('Booking details are not ready.');
    const sent = attempt ?? { appointmentTime, walkIn, selection, originWalkinId };
    // If session storage is unavailable, do not send a request whose retry key
    // would disappear on reload. The user keeps every selection on screen.
    try {
      sessionStorage.setItem(key.current, JSON.stringify({ attempt: sent }));
    } catch {
      throw new Error(
        'This browser could not keep the booking request. Keep this window open and try again.',
      );
    }
    lock.current = true;
    setBusy(true);
    setAttempt(sent);
    setError(null);
    const current = generation.current;
    const savedKey = key.current;
    try {
      const result = await bookAppointmentForPatient({
        patientId,
        clinicId,
        appointmentTime: sent.appointmentTime,
        ...(sent.walkIn ? { walkIn: true } : {}),
        descriptors: {
          ...sent.selection,
          ...(sent.originWalkinId != null ? { originWalkinId: sent.originWalkinId } : {}),
        },
      });
      // Receipt is for this exact key even if the user switched patient meanwhile.
      sessionStorage.setItem(savedKey, JSON.stringify({ attempt: sent, result }));
      if (current === generation.current) {
        completedKey.current = savedKey;
        setRestored(result);
        void readSaved(result.appointment.id);
      }
      return result;
    } catch (cause) {
      // 400 is definitive no-write validation; 409 is also no new admission.
      // Transport/5xx/auth outcomes retain the immutable request for exact retry.
      if (cause instanceof PortalApiError && (cause.status === 400 || cause.status === 409)) {
        sessionStorage.removeItem(savedKey);
        if (current === generation.current) {
          setAttempt(null);
          setSelection({ ...sent.selection, bookingRequestId: crypto.randomUUID() });
        }
      }
      throw new Error(
        safeErrorMessage(
          cause,
          'Booking outcome is not yet confirmed. Retry the saved request; your day and doctor choices are kept.',
        ),
      );
    } finally {
      if (current === generation.current) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  const update = (patch: Partial<PatientBookingSelection>) =>
    setSelection((value) => (value ? { ...value, ...patch } : value));
  const fields = enabled ? (
    <section aria-label="Booking details" className="ac-wizard__card">
      {!ready ? (
        <>
          <p role={error ? 'alert' : 'status'}>{error ?? 'Loading booking details...'}</p>
          {error && (
            <AcButton size="sm" onClick={() => setReload((n) => n + 1)}>
              Try again
            </AcButton>
          )}
        </>
      ) : (
        <>
          {attempt && !restored && (
            <p role="status">Your booking request is kept. Retry to confirm the same visit.</p>
          )}
          <p data-testid="booking-patient-context">
            {bookingSummary?.umr ? `${bookingSummary.umr} · ` : ''}
            {options?.patientContext === 'new'
              ? 'New patient'
              : options?.patientContext === 'returning'
                ? 'Returning patient'
                : 'Patient history not recorded'}
          </p>
          {careLabel!==null&&<p data-testid="booking-care-context">Follow-up · {careLabel}</p>}
          <BookingDescriptorFields
            options={options}
            selection={selection}
            clinicId={clinicId}
            clinicLabel={
              clinic?.options.find((option) => option.clinicId === clinicId)?.clinicLabel
            }
            disabled={busy || attempt !== null}
            update={update}
            compact={Boolean(bookingSummary)}
          />
          {error && <p role="alert">{error}</p>}
        </>
      )}
    </section>
  ) : null;
  const summary =
    enabled && restored ? (
      <section aria-label="Saved booking details">
        {savedDetails ? (
          <dl className="ac-booking-summary">
            {savedDetails.durationMinutes != null && <><dt>Time needed</dt><dd>{savedDetails.durationMinutes} min</dd></>}
            <dt>Department</dt>
            <dd>{savedDetails.department ?? 'Not recorded'}</dd>
            <dt>Treatment</dt>
            <dd>{savedDetails.serviceName ?? 'Not recorded'}</dd>
            <dt>Appointment type</dt>
            <dd>
              {VISIT_TYPES.find(([value]) => value === savedDetails.visitType)?.[1] ??
                'Not recorded'}
            </dd>
            <dt>Booking channel</dt>
            <dd>
              {CHANNELS.find(([value]) => value === savedDetails.bookingChannel)?.[1] ??
                'Not recorded'}
            </dd>
          </dl>
        ) : (
          <p role="status">{error ?? 'Reading saved booking details...'}</p>
        )}
        {error && (
          <AcButton size="sm" onClick={() => void readSaved(restored.appointment.id)}>
            Read saved details again
          </AcButton>
        )}
      </section>
    ) : null;
  return {
    fields,
    summary,
    book,
    attempt,
    restored,
    clearConfirmed,
    slotSelection: selection?.durationMinutes !== undefined && selection.doctorId !== null
      ? {doctorId: selection.doctorId, durationMinutes: selection.durationMinutes} : undefined,
    selectionIdentity: JSON.stringify([
      selection?.durationMinutes,
      selection?.doctorId,
      selection?.department,
      selection?.serviceCode,
      selection?.visitType,
      selection?.bookingChannel,
    ]),
    ready: !enabled || ready,
    valid:
      !enabled ||
      Boolean(
        selection &&
        selection.bookingChannel !== null &&
        (selection.durationMinutes === undefined || selection.doctorId !== null) &&
        (selection.department === null || selection.serviceCode !== null),
      ),
  };
}

/** Shared frozen descriptor controls for creating and editing a booking. */
export function BookingDescriptorFields({
  options,
  selection,
  clinicId,
  clinicLabel,
  disabled,
  update,
  compact = false,
  fixedChannel,
}: {
  options: PatientBookingOptions | null;
  selection: PatientBookingSelection | null;
  clinicId: number | null;
  clinicLabel?: string | null;
  disabled: boolean;
  update: (patch: Partial<PatientBookingSelection>) => void;
  compact?: boolean;
  fixedChannel?: string | null;
}) {
  return (
    <fieldset disabled={disabled} className="ac-booking-fields">
      <legend className="sr-only">Booking details</legend>
      <FormField id="patient-booking-branch" label="Branch">
        <AcSelect id="patient-booking-branch" value={clinicId ?? ''} disabled>
          <option value={clinicId ?? ''}>{clinicLabel || 'Clinic name unavailable'}</option>
        </AcSelect>
      </FormField>
      <FormField id="patient-booking-department" label="Department">
        <AcSelect
          id="patient-booking-department"
          value={selection?.department ?? ''}
          onChange={(e) => update({ department: e.target.value || null, serviceCode: null })}
        >
          <option value="">Not specified</option>
          {[...new Set(options?.treatments.map((t) => t.serviceGroup) ?? [])]
            .sort()
            .map((department) => (
              <option key={department}>{department}</option>
            ))}
          {selection?.department &&
            !options?.treatments.some((t) => t.serviceGroup === selection.department) && (
              <option>{selection.department}</option>
            )}
        </AcSelect>
      </FormField>
      <FormField id="patient-booking-treatment" label="Treatment">
        <AcSelect
          id="patient-booking-treatment"
          value={selection?.serviceCode ?? ''}
          disabled={!selection?.department || disabled}
          onChange={(e) => update({ serviceCode: e.target.value || null })}
        >
          <option value="">
            {selection?.department ? 'Choose treatment' : 'Choose department first'}
          </option>
          {options?.treatments
            .filter((t) => t.serviceGroup === selection?.department)
            .map((t) => (
              <option key={t.serviceCode} value={t.serviceCode}>
                {t.serviceName}
              </option>
            ))}
          {selection?.serviceCode &&
            !options?.treatments.some((t) => t.serviceCode === selection.serviceCode) && (
              <option value={selection.serviceCode}>Selected treatment</option>
            )}
        </AcSelect>
      </FormField>
      <FormField id="patient-booking-doctor" label={compact ? 'Doctor' : 'Eligible doctor'}>
        <AcSelect
          id="patient-booking-doctor"
          value={selection?.doctorId ?? ''}
          onChange={(e) => update({ doctorId: e.target.value ? Number(e.target.value) : null })}
        >
          <option value="">Unassigned</option>
          {options?.doctors.map((doctor) => (
            <option key={doctor.id} value={doctor.id}>
              {doctor.name}
            </option>
          ))}
          {selection?.doctorId && !options?.doctors.some((d) => d.id === selection.doctorId) && (
            <option value={selection.doctorId}>Selected doctor</option>
          )}
        </AcSelect>
        {selection?.durationMinutes !== undefined && <span className="ac-field__hint">Time needed · {selection.durationMinutes} min</span>}
      </FormField>
      {fixedChannel !== undefined ? (
        <FormField id="patient-booking-channel" label="Booking channel">
          <p id="patient-booking-channel">
            {CHANNELS.find(([token]) => token === fixedChannel)?.[1] ??
              fixedChannel ??
              'Not recorded'}
          </p>
        </FormField>
      ) : (
        <FormField id="patient-booking-channel" label="Booking channel">
          <AcSelect
            id="patient-booking-channel"
            value={selection?.bookingChannel ?? ''}
            onChange={(e) => update({ bookingChannel: e.target.value || null })}
          >
            <option value="">Choose a channel</option>
            {CHANNELS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </AcSelect>
        </FormField>
      )}
    </fieldset>
  );
}
